// İSG uzmanı portalı — Kullanıcılar (Users) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: her Kullanıcılar ucu `[ModuleAction("users", …)]` taşır;
// grant'ı olmayan principal → 403 (api-contract §12). Bu sonda yalnız UX görünürlüğüdür —
// hafif bir liste probu (`GET /users?page=1&pageSize=1`) çeker:
//   - 2xx  → erişim var (granted) → Kullanıcılar kabuğu + menü görünür
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//
// Ayarlar (§10) / Admin (§9) / iş modülü (Permits vb.) sondalarıyla aynı desendir;
// tenant kullanıcı yönetimi olarak AYRIDIR. Gerçek modül Value = `users` (küçük harf; §12) —
// probun bağladığı uç `list` aksiyonudur; yetkisiz principal buton/route göremez.

import { ApiError, usersApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { usersKeys } from './usersKeys';

export type UsersAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface UsersAccess {
  status: UsersAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Kullanıcı yönetimi erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı)
 * varsayılır. 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 404/503 (servis/veri)
 * ayrımı korunur.
 */
export function useUsersAccess(): UsersAccess {
  const query = useQuery({
    queryKey: usersKeys.access(),
    queryFn: () => usersApi.listUsers({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: UsersAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
